import { createContext, type ReactNode, useContext } from "react";

import type { CopilotApi } from "../copilot/api";
import type { ConversationsApi } from "./api";
import type { ConnectMessaging } from "./thread-client";

// What the staff workspace needs from outside (CHT-732): the typed API, the Stream boundary, the clock and an id maker. The admin app
// supplies the real ones (`admin-runtime.ts`); a test supplies a fake API and a fake messaging connector.

export type ConversationsRuntime = Readonly<{
  api: ConversationsApi;
  /** The private Staff Copilot (CHT-733). It has no way to address the customer thread: see `copilot/api.ts`. */
  copilot: CopilotApi;
  connectMessaging: ConnectMessaging;
  now: () => Date;
  /** A new lowercase uuid v4: a message id (contract section 3.5) or an idempotency key. */
  newId: () => string;
  /** How often the inbox refreshes by itself, in ms. 0 turns it off (tests). Default 30,000. */
  inboxRefreshMs?: number;
  /** How often the open conversation's context is read again while the viewer owns the handoff, in ms. 0 turns it off. Default 10,000. */
  contextPollMs?: number;
}>;

const RuntimeContext = createContext<ConversationsRuntime | null>(null);

export function ConversationsRuntimeProvider({
  value,
  children,
}: Readonly<{ value: ConversationsRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function useConversationsRuntime(): ConversationsRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) {
    throw new Error("The concierge workspace needs a ConversationsRuntimeProvider.");
  }
  return runtime;
}
